import { type Filters } from 'common/components/FilterBar/types';
import { type ViewColumn } from 'common/types/viewColumn';
import { type FilterMetadata } from 'common/types/filterMetadata';

/**
 * Generate filter metadata synchronously using data already available in Grid.
 * This is more efficient than the async generateFilterMetadata function as it
 * doesn't require MetadataProvider calls or dataset metadata fetching.
 *
 * @param vifFilters - The VIF filters to process
 * @param vifColumns - The VIF columns for display name fallback
 * @param columnMetadata - The dataset metadata columns for display name fallback
 * @returns Array of FilterMetadata objects
 */
export const generateFilterMetadataFromGrid = (
  vifFilters: Filters,
  vifColumns: ViewColumn[],
  columnMetadata: ViewColumn[],
): FilterMetadata[] => {
  // Handle null/undefined inputs gracefully
  if (!vifFilters) {
    return [];
  }

  const safeVifColumns = vifColumns || [];
  const safeColumnMetadata = columnMetadata || [];

  return vifFilters
    .filter((filter) => filter.arguments != null && Object.keys(filter.arguments || {}).length > 0)
    .map((filter) => {
      const fieldName = filter.columns?.[0]?.fieldName || '';
      let displayName = '';

      if (filter.displayName) {
        displayName = filter.displayName;
      } else if (filter.columns?.[0]?.displayName) {
        displayName = filter.columns[0].displayName;
      } else {
        // Fall back to VIF columns (visible columns only) then dataset metadata then field name
        const vifColumn = safeVifColumns.find((col: ViewColumn) => col.fieldName === fieldName);
        if (vifColumn?.name) {
          displayName = vifColumn.name;
        } else {
          const datasetColumn = safeColumnMetadata.find((col) => col.fieldName === fieldName);
          displayName = datasetColumn?.name || fieldName;
        }
      }

      return {
        fieldName,
        displayName,
        dataTypeName: filter.dataTypeName,
      };
    });
};
